/**
 * Défi privé en direct (0704) : les bulles « avatar ±HORA » en haut de l'écran
 * quand un joueur valide (ou efface) une ligne de brouillon, et le panneau
 * facultatif des progrès de chacun en HORA.
 */

import { useEffect, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';
import { usePrivateLiveProgress, type PrivateLiveProgressRow } from '@/lib/private-live';
import {
  formatDelta,
  progressBubbles,
  progressStandings,
  readProgressPref,
  writeProgressPref,
  type ProgressBubble,
} from './live-progress';

/** Durée d'une bulle à l'écran. */
export const BUBBLE_MS = 3_200;
/** Bulles visibles au plus. */
const MAX_BUBBLES = 4;

/** Le direct d'un défi privé : relit les progrès, fait apparaître les bulles. */
export function useLiveProgress(challengeId: string | null, active: boolean) {
  const query = usePrivateLiveProgress(challengeId, active);
  const prev = useRef<PrivateLiveProgressRow[] | null>(null);
  const [bubbles, setBubbles] = useState<ProgressBubble[]>([]);
  const rows = query.data;
  useEffect(() => {
    if (!rows) return;
    const fresh = progressBubbles(prev.current, rows, Date.now());
    prev.current = rows;
    if (fresh.length === 0) return;
    setBubbles((b) => [...b, ...fresh].slice(-MAX_BUBBLES));
    const t = window.setTimeout(() => {
      const ids = new Set(fresh.map((f) => f.id));
      setBubbles((b) => b.filter((x) => !ids.has(x.id)));
    }, BUBBLE_MS);
    return () => window.clearTimeout(t);
  }, [rows]);
  return { rows: rows ?? [], bubbles };
}

/** Les bulles, en haut au centre, sous la barre du live. */
export function ProgressBubbles({ bubbles }: { bubbles: ProgressBubble[] }) {
  return (
    <div
      className="native:top-[calc(env(safe-area-inset-top)+6.5rem)] pointer-events-none fixed inset-x-0 top-28 z-20 flex justify-center px-4"
      aria-live="polite"
      data-testid="progress-bubbles"
    >
      <div className="flex flex-wrap justify-center gap-1.5">
        {bubbles.map((b) => (
          <span
            key={b.id}
            className={cn(
              'motion-safe:animate-bounce-in flex items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 text-xs font-bold shadow-md ring-1',
              b.delta < 0
                ? 'bg-[#3a1220] text-[#ff9aa8] ring-[#ff7a7a]/40'
                : 'bg-[#0b2a1d] text-[#6ee7b7] ring-[#34d399]/40',
            )}
            data-testid="progress-bubble"
          >
            <AvatarCircle name={b.name} src={b.avatarUrl} size="xs" />
            <span className="sr-only">{b.name} : </span>
            {formatDelta(b.delta)}
            <span className="sr-only"> HORA</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Bouton « Progrès » : affiche ou masque le panneau (mémorisé sur l'appareil). */
export function useProgressPanelToggle() {
  const [on, setOn] = useState(readProgressPref);
  const toggle = () =>
    setOn((v) => {
      writeProgressPref(!v);
      return !v;
    });
  return { on, toggle };
}

export function ProgressToggleButton({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      aria-label={on ? 'Masquer les progrès des joueurs' : 'Voir les progrès des joueurs'}
      className={cn(
        'flex h-11 min-w-11 items-center justify-center gap-1 rounded-full px-3 text-xs font-bold',
        on ? 'bg-[#f4c64e] text-[#061433]' : 'bg-[rgba(255,255,255,0.1)] text-white',
      )}
      data-testid="progress-toggle"
    >
      <Users className="h-4 w-4" aria-hidden />
      <span className="hidden sm:inline">Progrès</span>
    </button>
  );
}

/** Le panneau : chaque joueur, son total d'HORA et une barre. */
export function ProgressPanel({ rows, unit }: { rows: PrivateLiveProgressRow[]; unit: string }) {
  const standings = progressStandings(rows);
  if (standings.length === 0) return null;
  const top = Math.max(1, ...standings.map((r) => r.total));
  return (
    <div
      className="mb-3 rounded-2xl bg-[rgba(6,20,51,0.78)] p-3 ring-1 ring-[rgba(255,255,255,0.1)]"
      data-testid="progress-panel"
    >
      <p className="font-display mb-2 text-xs font-bold uppercase tracking-wider text-[#f4c64e]">
        Progrès en direct
      </p>
      <ul className="space-y-1.5">
        {standings.map((r) => (
          <li key={r.key} className="flex items-center gap-2 text-sm text-white">
            <AvatarCircle name={r.name} src={r.avatar_url} size="xs" />
            <span className={cn('w-24 truncate', r.is_me && 'font-bold')}>
              {r.is_me ? 'Toi' : r.name}
            </span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[rgba(255,255,255,0.12)]">
              <span
                className="block h-full rounded-full bg-[#5aa9ff] transition-[width] duration-700"
                style={{ width: `${Math.round((r.total / top) * 100)}%` }}
              />
            </span>
            <span className="font-display w-16 shrink-0 text-right text-xs font-extrabold tabular-nums text-[#f4c64e]">
              {r.total} {unit}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
